import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../domain/service/payment/payment.service';
import { PaymentDashboard, ReturnSummary, Payment, PaymentStatus, ScheduledPayment } from '../../../domain/model/payment/payment.model';
import { NativeIoService } from '../../../core/native/native-io.service';

type PaymentTab = 'RETURNS' | 'SCHEDULED' | 'RECEIPTS';

@Component({
  selector: 'app-payments-dashboard',
  templateUrl: './payments-dashboard.component.html',
  styleUrls: ['./payments-dashboard.component.css']
})
export class PaymentsDashboardComponent implements OnInit {
  dashboard: PaymentDashboard | null = null;
  loading = true;
  error: string | null = null;

  activeTab: PaymentTab = 'RETURNS';

  // Basket of return ids queued for payment. Persisted to sessionStorage so it survives
  // navigating away from the dashboard and back; cleared after a completed payment.
  static readonly BASKET_KEY = 'tdt.web.basket';
  basket: number[] = [];

  // Filters
  filters = { year: 'ALL', returnType: 'ALL', status: 'ALL' };

  // Pagination
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  constructor(
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private nativeIo: NativeIoService
  ) { }

  // Scheduled payments (SCHEDULED tab)
  scheduledPayments: ScheduledPayment[] = [];
  scheduledLoading = false;

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('tab') === 'scheduled') {
      this.activeTab = 'SCHEDULED';
    }
    this.restoreBasket();
    this.loadDashboard();
    this.loadScheduledPayments();
  }

  loadScheduledPayments(): void {
    this.scheduledLoading = true;
    this.paymentService.listScheduledPayments().subscribe({
      next: (rows) => { this.scheduledPayments = rows ?? []; this.scheduledLoading = false; },
      error: () => { this.scheduledPayments = []; this.scheduledLoading = false; }
    });
  }

  cancelScheduled(sp: ScheduledPayment): void {
    if (sp.status !== 'PENDING') { return; }
    this.paymentService.cancelScheduledPayment(sp.id).subscribe({
      next: () => this.loadScheduledPayments(),
      error: () => { this.error = 'Could not cancel the scheduled payment. Please try again.'; }
    });
  }

  private restoreBasket(): void {
    try {
      const raw = sessionStorage.getItem(PaymentsDashboardComponent.BASKET_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      this.basket = Array.isArray(ids) ? ids.filter((n: any) => typeof n === 'number') : [];
    } catch {
      this.basket = [];
    }
  }

  private persistBasket(): void {
    try {
      sessionStorage.setItem(PaymentsDashboardComponent.BASKET_KEY, JSON.stringify(this.basket));
    } catch {
      /* sessionStorage unavailable — in-memory basket still works for this view */
    }
  }

  loadDashboard(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getDashboard().subscribe({
      next: (data) => {
        this.dashboard = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load payment dashboard. Please try again.';
        this.loading = false;
        console.error('Error loading dashboard:', err);
      }
    });
  }

  setTab(tab: PaymentTab): void {
    this.activeTab = tab;
    this.pageIndex = 0;
  }

  // ---------- Source lists ----------
  get returns(): ReturnSummary[] {
    return this.dashboard?.outstandingReturns ?? [];
  }

  get receipts(): Payment[] {
    return this.dashboard?.recentPayments ?? [];
  }

  // ---------- Filter option lists ----------
  get yearOptions(): string[] {
    const set = new Set<string>();
    this.returns.forEach(r => {
      const y = this.yearOf(r);
      if (y) set.add(y);
    });
    return Array.from(set).sort().reverse();
  }

  get returnTypeOptions(): string[] {
    return Array.from(new Set(this.returns.map(r => r.type).filter(Boolean)));
  }

  get statusOptions(): string[] {
    return Array.from(new Set(this.returns.map(r => r.status).filter(Boolean)));
  }

  private yearOf(r: ReturnSummary): string {
    if (r.dueDate) {
      const d = new Date(r.dueDate);
      if (!isNaN(d.getTime())) return d.getFullYear().toString();
    }
    const m = (r.period || '').match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  // ---------- Filtering ----------
  get filteredReturns(): ReturnSummary[] {
    return this.returns.filter(r =>
      (this.filters.year === 'ALL' || this.yearOf(r) === this.filters.year) &&
      (this.filters.returnType === 'ALL' || r.type === this.filters.returnType) &&
      (this.filters.status === 'ALL' || r.status === this.filters.status)
    );
  }

  get hasActiveFilters(): boolean {
    return this.filters.year !== 'ALL' || this.filters.returnType !== 'ALL' || this.filters.status !== 'ALL';
  }

  /** Count of non-default filters, shown as a badge on the mobile trigger. */
  get activeFilterCount(): number {
    return ['year', 'returnType', 'status']
      .filter(k => this.filters[k as 'year' | 'returnType' | 'status'] !== 'ALL').length;
  }

  /** Mobile-only: the filter selects live in a bottom-sheet modal. */
  mobileFiltersOpen = false;
  openMobileFilters(): void { this.mobileFiltersOpen = true; }
  closeMobileFilters(): void { this.mobileFiltersOpen = false; }

  onFilterChange(): void {
    this.pageIndex = 0;
  }

  clearFilter(key: 'year' | 'returnType' | 'status'): void {
    this.filters[key] = 'ALL';
    this.pageIndex = 0;
  }

  clearAllFilters(): void {
    this.filters = { year: 'ALL', returnType: 'ALL', status: 'ALL' };
    this.pageIndex = 0;
  }

  // ---------- Pagination ----------
  get pagedReturns(): ReturnSummary[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredReturns.slice(start, start + this.pageSize);
  }

  get rangeLabel(): string {
    const total = this.filteredReturns.length;
    if (total === 0) return '0 of 0';
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min(start + this.pageSize - 1, total);
    return `${start}-${end} of ${total}`;
  }

  get canPrev(): boolean { return this.pageIndex > 0; }
  get canNext(): boolean { return (this.pageIndex + 1) * this.pageSize < this.filteredReturns.length; }
  prevPage(): void { if (this.canPrev) this.pageIndex--; }
  nextPage(): void { if (this.canNext) this.pageIndex++; }
  onPageSizeChange(size: number): void { this.pageSize = size; this.pageIndex = 0; }

  // ---------- Stat chips ----------
  get monthsUnpaid(): number {
    return new Set(this.returns.filter(r => r.balanceDue > 0).map(r => this.yearOf(r) + '-' + r.period)).size;
  }
  get returnsUnpaid(): number {
    return this.returns.filter(r => r.balanceDue > 0 && (r.amountPaid ?? 0) === 0).length;
  }
  get partiallyPaid(): number {
    return this.returns.filter(r => (r.amountPaid ?? 0) > 0 && r.balanceDue > 0).length;
  }
  get pastDue(): number {
    const now = Date.now();
    return this.returns.filter(r => r.balanceDue > 0 && r.dueDate && new Date(r.dueDate).getTime() < now).length;
  }

  // ---------- Basket ----------
  isInBasket(id: number): boolean {
    return this.basket.includes(id);
  }

  /**
   * Chronological rule (mirrors the server guard): a return can only be added when every
   * older past-due return with a balance is already in the basket. Rolled-up returns never
   * reach this list (the server hides them), and future installments paid early pass
   * because nothing older is past due AND excluded.
   */
  basketBlockers(r: ReturnSummary): ReturnSummary[] {
    const now = Date.now();
    const due = r.dueDate ? new Date(r.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
    // Per home account (own returns key to null): shared returns order among themselves only.
    const ownerKey = (x: ReturnSummary) => x.shared ? x.owningAccountId : null;
    return this.returns.filter(o =>
      o.id !== r.id &&
      ownerKey(o) === ownerKey(r) &&
      !this.isInBasket(o.id) &&
      (o.balanceDue ?? 0) > 0 &&
      !!o.dueDate &&
      new Date(o.dueDate).getTime() < now &&
      new Date(o.dueDate).getTime() < due);
  }

  /** A return with nothing due can't be paid — it has to be filed (gross receipts entered) first. */
  hasNothingDue(r: ReturnSummary): boolean {
    return (r.balanceDue ?? 0) <= 0;
  }

  isBlockedFromBasket(r: ReturnSummary): boolean {
    return this.hasNothingDue(r) || this.basketBlockers(r).length > 0;
  }

  blockedTooltip(r: ReturnSummary): string {
    if (this.hasNothingDue(r)) { return 'Nothing due yet — open the return and enter your gross receipts first'; }
    const blockers = this.basketBlockers(r);
    if (!blockers.length) { return 'Add to cart'; }
    return 'Pay older past-due returns first: ' + blockers.map(b => b.period).join(', ');
  }

  addToBasket(id: number): void {
    const r = this.returns.find(x => x.id === id);
    if (r && this.isBlockedFromBasket(r)) { return; }
    if (!this.isInBasket(id)) {
      this.basket.push(id);
      this.persistBasket();
    }
  }
  removeFromBasket(id: number): void {
    this.basket = this.basket.filter(b => b !== id);
    this.persistBasket();
  }
  get basketCount(): number {
    return this.basket.length;
  }
  goToBasket(): void {
    if (this.basket.length === 0) return;
    this.router.navigate(['/app/payments/make-payment'], {
      queryParams: { returns: this.basket.join(',') }
    });
  }

  // ---------- Create next return ----------
  creatingReturn = false;

  createNextReturn(): void {
    if (this.creatingReturn) return;
    this.creatingReturn = true;
    this.error = null;
    this.paymentService.createNextReturns(1).subscribe({
      next: (res) => {
        this.creatingReturn = false;
        if (res.created > 0) {
          this.loadDashboard();
        } else {
          this.error = 'Your returns are already up to date — no new return was needed.';
        }
      },
      error: (err) => {
        this.creatingReturn = false;
        // The server explains a suspended filing schedule; show that instead of a generic retry.
        this.error = err?.error?.message || err?.error?.error || 'Could not create the next return. Please try again.';
        console.error('Error creating next return:', err);
      }
    });
  }

  // ---------- Row actions ----------
  viewReturn(r: ReturnSummary): void {
    this.router.navigate(['/app/payments/return', r.id]);
  }

  viewHistory(): void {
    this.router.navigate(['/app/payments/history']);
  }

  viewPaymentDetails(payment: Payment): void {
    this.router.navigate(['/app/payments/confirmation', payment.confirmationNumber]);
  }

  downloadReceipt(paymentId: number): void {
    this.nativeIo.saveDownload(
      `payment-receipt-${paymentId}.pdf`,
      this.paymentService.receiptUrl(paymentId),
      this.paymentService.downloadReceipt(paymentId),
    ).catch((err) => {
      console.error('Error downloading receipt:', err);
      this.error = 'Failed to download receipt. Please try again.';
    });
  }

  // ---------- Status styling ----------
  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'is-success';
      case 'partially paid':
      case 'partially_paid':
        return 'is-info';
      case 'unpaid':
      case 'pending':
        return 'is-pending';
      case 'past due':
      case 'past_due':
      case 'overdue':
      case 'failed':
        return 'is-danger';
      default:
        return 'is-neutral';
    }
  }
}
